import React, { useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { withNamespaces } from "react-i18next";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

const getCroppedBlobFromCropper = (cropper) =>
  new Promise((resolve) => {
    const canvas = cropper.getCroppedCanvas();
    if (!canvas) {
      resolve(null);
      return;
    }

    canvas.toBlob((blob) => resolve(blob), "image/png", 0.92);
  });

const LogoEditorModal = (props) => {
  const { t, cropModal, closeCropModal, onApply } = props;

  const cropperRef = useRef(null);
  const lastZoomRef = useRef(0);

  const [zoom, setZoom] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [aspectMode, setAspectMode] = useState("recommended");

  const computedAspect = useMemo(() => {
    if (aspectMode === "1:1") return 1;
    if (aspectMode === "4:1") return 4;
    if (aspectMode === "16:9") return 16 / 9;

    return cropModal?.type === "reduced" ? 1 : 4;
  }, [aspectMode, cropModal?.type]);

  const localPreviewUrl = useMemo(() => {
    if (cropModal?.previewUrl) return cropModal.previewUrl;
    if (cropModal?.file) return URL.createObjectURL(cropModal.file);
    return null;
  }, [cropModal?.previewUrl, cropModal?.file]);

  useEffect(() => {
    if (!localPreviewUrl) return;

    const createdHere = !!cropModal?.file && !cropModal?.previewUrl;
    return () => {
      if (createdHere && localPreviewUrl.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(localPreviewUrl);
        } catch (_) {}
      }
    };
  }, [localPreviewUrl, cropModal?.file, cropModal?.previewUrl]);

  useEffect(() => {
    if (cropModal?.isOpen) {
      setZoom(0);
      setIsApplying(false);
      lastZoomRef.current = 0;
    }
  }, [cropModal?.isOpen]);

  useEffect(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper || !cropModal?.isOpen) return;

    const delta = zoom - lastZoomRef.current;
    if (delta === 0) return;

    cropper.zoom(delta * 0.5);
    lastZoomRef.current = zoom;
  }, [zoom, cropModal?.isOpen]);

  const handleApply = async () => {
    if (!cropModal?.type || !cropModal?.file) return;

    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    try {
      setIsApplying(true);

      const blob = await getCroppedBlobFromCropper(cropper);
      if (!blob) {
        setIsApplying(false);
        return;
      }

      const safeBaseName = (cropModal.file.name || "logo").replace(
        /\.[^/.]+$/,
        ""
      );
      const outName = `${safeBaseName}_${cropModal.type}_cropped.png`;

      const processedFile = new File([blob], outName, { type: "image/png" });

      onApply(processedFile);
      closeCropModal();
    } catch (e) {
      console.error(e);
      setIsApplying(false);
    }
  };

  return (
    <Modal isOpen={cropModal.isOpen} toggle={closeCropModal} size="lg" centered>
      <ModalHeader toggle={closeCropModal}>{t("Adjust Logo")}</ModalHeader>

      <ModalBody>
        {!!cropModal?.reason && (
          <div className="mb-3">
            <strong>{t("Aspect ratio mismatch")}</strong>
            <div className="text-muted" style={{ marginTop: 6 }}>
              {cropModal.reason}
            </div>
          </div>
        )}

        {localPreviewUrl ? (
          <>
            <div className="d-flex align-items-center gap-2 mb-2">
              <span style={{ minWidth: 90 }}>{t("Crop ratio")}</span>
              <select
                className="form-select"
                style={{ maxWidth: 220 }}
                value={aspectMode}
                onChange={(e) => setAspectMode(e.target.value)}
              >
                <option value="recommended">{t("Recommended")}</option>
                <option value="free">{t("Free")}</option>
                <option value="4:1">4:1</option>
                <option value="1:1">1:1</option>
                <option value="16:9">16:9</option>
              </select>

              <span className="text-muted" style={{ fontSize: 12 }}>
                {cropModal?.type === "reduced"
                  ? t("Recommended: 1:1")
                  : t("Recommended: 4:1")}
              </span>
            </div>

            <div
              style={{
                position: "relative",
                width: "100%",
                height: "min(60vh, 520px)",
                background: "#111",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <Cropper
                key={`${cropModal?.type || "x"}-${aspectMode}-${
                  localPreviewUrl || "img"
                }`}
                src={localPreviewUrl}
                style={{ height: "100%", width: "100%" }}
                ref={cropperRef}
                guides={true}
                background={false}
                responsive={true}
                checkOrientation={false}
                autoCropArea={1}
                dragMode="move"
                viewMode={1}
                cropBoxMovable={true}
                cropBoxResizable={true}
                toggleDragModeOnDblclick={false}
                initialAspectRatio={
                  aspectMode === "free" ? NaN : computedAspect
                }
                aspectRatio={aspectMode === "free" ? NaN : computedAspect}
              />
            </div>

            {aspectMode === "free" && (
              <div className="text-warning mt-2" style={{ fontSize: 12 }}>
                {t("Free crop may not fit the UI area perfectly.")}
              </div>
            )}
          </>
        ) : null}

        <div className="d-flex align-items-center gap-2 mt-3">
          <span style={{ minWidth: 60 }}>{t("Zoom")}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          outline
          color="primary"
          onClick={closeCropModal}
          disabled={isApplying}
        >
          {t("Cancel")}
        </Button>

        <Button
          color="primary"
          onClick={handleApply}
          disabled={isApplying || !cropModal?.file}
        >
          {isApplying ? t("Applying...") : t("Apply")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default withNamespaces()(LogoEditorModal);
